import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { updateTask, deleteTask } from "../lib/offlineTasks";
import { deleteCapture } from "../lib/offlineCaptures";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { CaptureEditDialog } from "./CapturePreviewDialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Trash, Plus, LinkSimple, X, CircleNotch, NotePencil } from "@phosphor-icons/react";

// Shared edit dialog for a single task, opened by clicking any task
// representation across the app (TasksPage rows, Schedule month/week chips,
// Schedule day-view timeline blocks). Lets the user edit every field
// (title, detailed description, due date/time, completion) in one
// place instead of only toggling a checkbox or deleting. Autosave is a
// simple fixed 2-second interval that persists whatever's changed since
// the last save - deliberately not tied to a typing-debounce or an
// in-flight-save coalescing queue (fewer moving parts = fewer ways for a
// save to be silently skipped/lost). Also saves immediately when the
// dialog closes or (on mobile) the page is hidden/backgrounded, same as
// the Notes editor.
export function TaskDetailDialog({ task, open, onOpenChange, onSaved, onDeleted }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState("");
  const [completed, setCompleted] = useState(false);
  const [saving, setSaving] = useState(false);

  // Notes attached to this task/schedule item - stored on the task doc as
  // just a list of capture ids (note_ids); this dialog fetches each one's
  // details for display. A note can be detached without ever deleting the
  // underlying capture (detach only edits the task's note_ids list).
  const [attachedNotes, setAttachedNotes] = useState([]);
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachQuery, setAttachQuery] = useState("");
  const [attachResults, setAttachResults] = useState([]);
  const [attachLoading, setAttachLoading] = useState(false);
  const [creatingNote, setCreatingNote] = useState(false);
  const attachDebounceRef = useRef(null);
  // Opening a linked note used to navigate to /dashboard?openNote=<id>,
  // which took the user off the Tasks/Schedule page entirely - closing the
  // note then left them stranded on the Notes page instead of back on the
  // task/schedule they were editing. Rendering the same CaptureEditDialog
  // as a NESTED dialog here instead means opening/closing a note never
  // navigates anywhere - the user is always still looking at this exact
  // TaskDetailDialog underneath.
  const [viewingNote, setViewingNote] = useState(null);

  const fieldsRef = useRef({ title: "", description: "", dueDate: "", dueTime: "", completed: false });
  const savedRef = useRef({ title: "", description: "", dueDate: "", dueTime: "", completed: false });
  const taskIdRef = useRef(null);

  useEffect(() => {
    if (!task) return;
    const initial = {
      title: task.title || "",
      description: task.description || "",
      dueDate: task.due_date || "",
      dueTime: task.due_time || "",
      completed: !!task.completed,
    };
    setTitle(initial.title);
    setDescription(initial.description);
    setDueDate(initial.dueDate);
    setDueTime(initial.dueTime);
    setCompleted(initial.completed);
    fieldsRef.current = initial;
    savedRef.current = initial;
    taskIdRef.current = task.id;
  }, [task]);

  // Fetches the full details (title/type) of each attached note id for
  // display - keyed on the joined id list (not the raw array reference)
  // so a same-content note_ids array from a fresh task object (e.g. after
  // an unrelated due-date autosave) doesn't trigger a redundant refetch.
  const noteIdsKey = (task?.note_ids || []).join(",");
  useEffect(() => {
    const ids = task?.note_ids || [];
    if (ids.length === 0) {
      setAttachedNotes([]);
      return;
    }
    Promise.all(
      ids.map((id) => api.get(`/captures/${id}`).then((res) => res.data).catch(() => null))
    ).then((results) => setAttachedNotes(results.filter(Boolean)));
  }, [task?.id, noteIdsKey]);

  useEffect(() => {
    clearTimeout(attachDebounceRef.current);
    const trimmed = attachQuery.trim();
    if (!trimmed) {
      setAttachResults([]);
      setAttachLoading(false);
      return;
    }
    setAttachLoading(true);
    attachDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: trimmed } });
        setAttachResults(data.notes || []);
      } catch (e) {
        // Non-critical - just leave the results list as-is.
      } finally {
        setAttachLoading(false);
      }
    }, 300);
    return () => clearTimeout(attachDebounceRef.current);
  }, [attachQuery]);

  // Mobile browsers can suspend a backgrounded tab's JS timers before the
  // next 2-second tick gets a chance to fire, so force an immediate save
  // whenever the page is hidden/unloaded (mirrors CapturePreviewDialog).
  const performSaveRef = useRef(() => {});
  useEffect(() => {
    const saveIfHidden = () => {
      if (document.visibilityState === "hidden") performSaveRef.current();
    };
    document.addEventListener("visibilitychange", saveIfHidden);
    window.addEventListener("pagehide", saveIfHidden);
    return () => {
      document.removeEventListener("visibilitychange", saveIfHidden);
      window.removeEventListener("pagehide", saveIfHidden);
    };
  }, []);

  // If a save is already in flight when called again (2s tick, or the
  // dialog closing right as a tick's request is still pending), just wait
  // for it instead of firing a second overlapping request; then re-check
  // with fresh field values (avoids stale/out-of-order overwrites without
  // needing a separate pending-save flag or recursive follow-up call).
  const savingPromiseRef = useRef(null);

  const performSave = async () => {
    if (savingPromiseRef.current) await savingPromiseRef.current;
    const current = fieldsRef.current;
    if (!taskIdRef.current || !current.title.trim()) return;
    const saved = savedRef.current;
    const changed = current.title !== saved.title || current.description !== saved.description
      || current.dueDate !== saved.dueDate || current.dueTime !== saved.dueTime || current.completed !== saved.completed;
    if (!changed) return;
    const promise = (async () => {
      try {
        const data = await updateTask(taskIdRef.current, {
          title: current.title.trim(),
          description: current.description,
          due_date: current.dueDate || "",
          due_time: current.dueTime || "",
          completed: current.completed,
        });
        savedRef.current = current;
        onSaved(data);
      } catch (err) {
        toast.error(t("taskDetail.saveFailedError"));
      }
    })();
    savingPromiseRef.current = promise;
    await promise;
    if (savingPromiseRef.current === promise) savingPromiseRef.current = null;
  };
  performSaveRef.current = performSave;

  // The single autosave mechanism: check for unsaved changes every 2
  // seconds and persist them if found.
  useEffect(() => {
    if (!task) return;
    const interval = setInterval(() => performSaveRef.current(), 2000);
    return () => clearInterval(interval);
  }, [task]);

  const updateField = (key, value) => {
    fieldsRef.current = { ...fieldsRef.current, [key]: value };
  };

  const handleOpenChange = async (isOpen) => {
    if (!isOpen) await performSave();
    onOpenChange(isOpen);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await performSave();
      toast.success(t("taskDetail.savedSuccess"));
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTask(task.id);
      onDeleted(task.id);
      onOpenChange(false);
    } catch (err) {
      toast.error(t("tasks.deleteFailedError"));
    }
  };

  const handleCreateNote = async () => {
    setCreatingNote(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/notes/new`);
      setAttachedNotes((prev) => [...prev, data.note]);
      onSaved(data.task);
    } catch (e) {
      toast.error(t("taskDetail.noteActionFailedError"));
    } finally {
      setCreatingNote(false);
    }
  };

  const handleAttachExisting = async (note) => {
    try {
      const { data } = await api.post(`/tasks/${task.id}/notes`, { capture_id: note.id });
      // `note` here is a /api/search result (id/title/snippet/type only) -
      // fetch the full capture so it has everything CaptureEditPanel needs
      // (text_content_html, image_data, etc.) in case the user opens it
      // right away without the dialog's own note_ids-driven fetch effect
      // running again first.
      const { data: fullNote } = await api.get(`/captures/${note.id}`);
      setAttachedNotes((prev) => (prev.some((n) => n.id === note.id) ? prev : [...prev, fullNote]));
      onSaved(data);
      setAttachOpen(false);
      setAttachQuery("");
    } catch (e) {
      toast.error(t("taskDetail.noteActionFailedError"));
    }
  };

  const handleDetachNote = async (noteId) => {
    try {
      const { data } = await api.delete(`/tasks/${task.id}/notes/${noteId}`);
      setAttachedNotes((prev) => prev.filter((n) => n.id !== noteId));
      onSaved(data);
    } catch (e) {
      toast.error(t("taskDetail.noteActionFailedError"));
    }
  };

  const handleOpenNote = (noteId) => {
    const note = attachedNotes.find((n) => n.id === noteId);
    if (note) setViewingNote(note);
  };

  const handleNoteSaved = (updated) => {
    setAttachedNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
    setViewingNote(updated);
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteCapture(noteId);
      await api.delete(`/tasks/${task.id}/notes/${noteId}`).catch(() => {});
      setAttachedNotes((prev) => prev.filter((n) => n.id !== noteId));
      setViewingNote(null);
      toast.success(t("dashboard.movedToTrashSuccess"));
    } catch (e) {
      toast.error(t("taskDetail.noteActionFailedError"));
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="rounded-none max-w-md" data-testid="task-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{t("taskDetail.title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox
              data-testid="task-detail-completed-checkbox"
              checked={completed}
              onCheckedChange={(v) => {
                const val = !!v;
                setCompleted(val);
                updateField("completed", val);
              }}
            />
            <Input
              data-testid="task-detail-title-input"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                updateField("title", e.target.value);
              }}
              placeholder={t("tasks.newTaskPlaceholder")}
              className={`rounded-none flex-1 font-bold text-sm md:text-base ${completed ? "line-through text-muted-foreground" : ""}`}
            />
          </div>

          <Textarea
            data-testid="task-detail-description-input"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              updateField("description", e.target.value);
            }}
            placeholder={t("taskDetail.descriptionPlaceholder")}
            className="rounded-none min-h-[250px] md:min-h-[100px] font-bold text-sm md:text-base"
          />

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs md:text-sm font-mono-tag font-bold text-muted-foreground">{t("taskDetail.dueDateLabel")}</label>
              <Input
                data-testid="task-detail-due-date-input"
                type="date"
                value={dueDate}
                onChange={(e) => {
                  setDueDate(e.target.value);
                  updateField("dueDate", e.target.value);
                }}
                className="rounded-none font-bold text-sm md:text-base"
              />
            </div>
            <div>
              <label className="text-xs md:text-sm font-mono-tag font-bold text-muted-foreground">{t("taskDetail.dueTimeLabel")}</label>
              <Input
                data-testid="task-detail-due-time-input"
                type="time"
                value={dueTime}
                onChange={(e) => {
                  setDueTime(e.target.value);
                  updateField("dueTime", e.target.value);
                }}
                className="rounded-none font-bold text-sm md:text-base"
              />
            </div>
          </div>

          <div className="space-y-2" data-testid="task-detail-notes-section">
            <div className="flex items-center justify-between">
              <label className="text-xs md:text-sm font-mono-tag font-bold text-muted-foreground">{t("taskDetail.notesLabel")}</label>
              <div className="flex items-center gap-1.5">
                <Button
                  type="button"
                  data-testid="task-detail-new-note-button"
                  variant="outline"
                  size="sm"
                  disabled={creatingNote}
                  onClick={handleCreateNote}
                  className="rounded-none h-7 text-xs gap-1"
                >
                  {creatingNote ? <CircleNotch size={12} className="animate-spin" /> : <Plus size={12} />} {t("taskDetail.newNoteButton")}
                </Button>
                <Button
                  type="button"
                  data-testid="task-detail-attach-note-button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAttachOpen((v) => !v)}
                  className="rounded-none h-7 text-xs gap-1"
                >
                  <LinkSimple size={12} /> {t("taskDetail.attachNoteButton")}
                </Button>
              </div>
            </div>

            {attachOpen && (
              <div className="border border-border p-2 space-y-2" data-testid="task-detail-attach-search-panel">
                <Input
                  autoFocus
                  data-testid="task-detail-attach-search-input"
                  value={attachQuery}
                  onChange={(e) => setAttachQuery(e.target.value)}
                  placeholder={t("taskDetail.attachSearchPlaceholder")}
                  className="rounded-none h-8 text-sm"
                />
                {attachLoading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground py-1" data-testid="task-detail-attach-loading">
                    <CircleNotch size={12} className="animate-spin" /> {t("search.searching")}
                  </div>
                ) : attachQuery.trim() && attachResults.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-1" data-testid="task-detail-attach-empty">{t("search.noResults")}</p>
                ) : (
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {attachResults.filter((n) => !attachedNotes.some((a) => a.id === n.id)).map((note) => (
                      <button
                        key={note.id}
                        type="button"
                        data-testid={`task-detail-attach-result-${note.id}`}
                        onClick={() => handleAttachExisting(note)}
                        className="w-full flex items-center gap-1.5 text-left text-xs px-2 py-1.5 hover:bg-secondary transition-colors truncate"
                      >
                        <NotePencil size={12} className="text-primary shrink-0" />
                        <span className="truncate">{note.title || t("captureCard.untitledFallback")}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {attachedNotes.length > 0 && (
              <div className="space-y-1" data-testid="task-detail-attached-notes-list">
                {attachedNotes.map((note) => (
                  <div
                    key={note.id}
                    data-testid={`task-detail-attached-note-${note.id}`}
                    className="flex items-center justify-between gap-2 border border-border px-2 py-1.5 bg-secondary/40"
                  >
                    <button
                      type="button"
                      data-testid={`task-detail-open-note-${note.id}`}
                      onClick={() => handleOpenNote(note.id)}
                      className="flex-1 flex items-center gap-1.5 text-left text-xs font-semibold truncate hover:underline min-w-0"
                    >
                      <NotePencil size={12} className="text-primary shrink-0" />
                      <span className="truncate">{note.title || t("captureCard.untitledFallback")}</span>
                    </button>
                    <button
                      type="button"
                      data-testid={`task-detail-detach-note-${note.id}`}
                      onClick={() => handleDetachNote(note.id)}
                      className="text-muted-foreground hover:text-destructive shrink-0"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="flex items-center sm:justify-between gap-2">
            <Button
              type="button"
              data-testid="task-detail-delete-button"
              variant="ghost"
              onClick={handleDelete}
              className="rounded-none text-destructive hover:text-destructive gap-2 font-bold text-sm md:text-base"
            >
              <Trash size={16} /> {t("taskDetail.deleteButton")}
            </Button>
            <Button type="submit" data-testid="task-detail-save-button" disabled={saving} className="rounded-none font-bold text-sm md:text-base">
              {t("taskDetail.saveButton")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <CaptureEditDialog
        capture={viewingNote}
        open={!!viewingNote}
        onOpenChange={(o) => !o && setViewingNote(null)}
        onDelete={handleDeleteNote}
        onSaved={handleNoteSaved}
      />
    </Dialog>
  );
}
